import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Home, FileText, DollarSign, Image as ImageIcon, Newspaper, Library as LibraryIcon, Users, LogOut, User, ExternalLink, Archive, Camera, Loader2, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import useAuthStore from '../store/authStore';
import api, { getMediaBlobUrl } from '../utils/api';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_0a775788-3b72-4cac-99b0-2ae8be67d728/artifacts/5ne6bwob_Alhaj_Halfan_Swai_Logo.png';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuthStore();
  const fileInputRef = useRef(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);

  const menuItems = [
    { path: '/admin', icon: Home, label: 'Dashboard' },
    { path: '/admin/meetings', icon: Archive, label: 'Meeting Archive' },
    { path: '/admin/agenda', icon: CalendarDays, label: 'Agenda' },
    { path: '/admin/minutes', icon: FileText, label: 'Minutes' },
    { path: '/admin/financial-statements', icon: DollarSign, label: 'Financial' },
    { path: '/admin/gallery', icon: ImageIcon, label: 'Gallery' },
    { path: '/admin/news', icon: Newspaper, label: 'News' },
    { path: '/admin/library', icon: LibraryIcon, label: 'Library' },
    { path: '/admin/users', icon: Users, label: 'Users' },
  ];

  // Load profile picture
  useEffect(() => {
    const loadProfilePicture = async () => {
      if (user?.profile_picture) {
        try {
          const url = await getMediaBlobUrl(user.profile_picture);
          setProfileImageUrl(url);
        } catch (error) {
          console.error('Failed to load profile picture');
        }
      }
    };
    loadProfilePicture();
    return () => {
      if (profileImageUrl) window.URL.revokeObjectURL(profileImageUrl);
    };
  }, [user?.profile_picture]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WEBP image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/auth/profile-picture', formData);
      updateUser({ profile_picture: response.data.profile_picture });
      
      const url = await getMediaBlobUrl(response.data.profile_picture);
      setProfileImageUrl(url);
      
      toast.success('Profile picture updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload profile picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  return (
    <div className="flex h-screen bg-slate-50">
      <aside className="w-72 bg-slate-900 text-white flex flex-col">
        {/* Integrated Brand Header */}
        <div className="relative overflow-hidden">
          {/* Background Pattern with Logo */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -top-10 -right-10 w-40 h-40">
              <img src={LOGO_URL} alt="" className="w-full h-full object-contain opacity-50" />
            </div>
            <div className="absolute -bottom-5 -left-5 w-24 h-24">
              <img src={LOGO_URL} alt="" className="w-full h-full object-contain opacity-30" />
            </div>
          </div>
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-600/20 via-transparent to-amber-600/10" />
          
          {/* Content */}
          <div className="relative p-6 border-b border-slate-700/50">
            <div className="flex items-center space-x-4">
              {/* Logo Container with Glow Effect */}
              <div className="relative">
                <div className="absolute inset-0 bg-orange-500/30 rounded-2xl blur-xl" />
                <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 p-2 rounded-2xl border border-slate-700/50 shadow-xl">
                  <img src={LOGO_URL} alt="AHSGA Logo" className="w-14 h-14 object-contain" />
                </div>
              </div>
              
              {/* Brand Text */}
              <div className="flex-1">
                <h1 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-white via-orange-100 to-amber-200 bg-clip-text text-transparent" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  AHSGA
                </h1>
                <div className="flex items-center space-x-2 mt-1">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-widest">Admin Portal</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-orange-600 to-amber-600 text-white shadow-lg shadow-orange-500/25'
                      : 'text-slate-300 hover:bg-slate-800/70 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
          
          {/* External Link */}
          <div className="mt-6 pt-4 border-t border-slate-700/50">
            <a
              href="https://baobaborphanage.org"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-800/70 hover:text-white transition-all"
            >
              <ExternalLink className="w-5 h-5" />
              <span className="font-medium text-sm">Baobab Orphanage Centre</span>
            </a>
          </div>
        </nav>

        {/* User Profile Section */}
        <div className="p-4 border-t border-slate-700/50 bg-slate-800/30">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div 
                className="relative group cursor-pointer"
                onClick={handleProfilePictureClick}
                title="Click to change profile picture"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center overflow-hidden ring-2 ring-orange-500/30 shadow-lg">
                  {uploadingPicture ? (
                    <Loader2 className="w-5 h-5 text-white animate-spin" />
                  ) : profileImageUrl ? (
                    <img src={profileImageUrl} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg font-bold text-white">{user?.full_name?.charAt(0)}</span>
                  )}
                </div>
                <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera className="w-5 h-5 text-white" />
                </div>
                <input 
                  ref={fileInputRef}
                  type="file" 
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">{user?.full_name}</p>
                <p className="text-xs text-orange-400">Administrator</p>
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center space-x-2 text-slate-300 hover:text-white transition-colors p-2.5 rounded-xl hover:bg-slate-700/50 border border-slate-700/50"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto flex flex-col">
        <div className="watermark-container flex-1">
          <div className="watermark-content max-w-7xl mx-auto p-6 md:p-8">
            {children}
          </div>
        </div>
        
        <footer className="portal-footer-dark portal-footer">
          <a 
            href="https://baobaborphanage.org" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            Baobab Orphanage Centre
          </a>
          <p className="text-xs text-slate-500 mt-1">© 2026 AHSGA. All rights reserved.</p>
        </footer>
      </main>
    </div>
  );
};

export default AdminLayout;
